/**
 * Where to land after an OIDC sign-in.
 *
 * Keycloak always sends the browser back to the app root (`redirect_uri` = `REACT_APP_URL`), so the
 * page someone was trying to reach — typically a deep link from an email, which opens in a fresh
 * tab where the per-tab session storage holds no session — travels through the sign-in in the OIDC
 * `state` and is restored by `onSigninCallback` in `src/app/index.tsx`.
 */

/**
 * Query parameters of an OIDC callback. They are never carried into a return target: a URL with
 * `code`/`error` + `state` is taken for a callback by react-oidc-context, and a spent one would be
 * replayed on the next load.
 */
const OIDC_CALLBACK_PARAMS = ['code', 'state', 'session_state', 'iss', 'error', 'error_description', 'error_uri'];

export interface ReturnToState {
  returnTo: string;
}

/** An in-app location (path + query + hash), minus any OIDC callback parameters. */
export function currentReturnTo(location: Pick<Location, 'pathname' | 'search' | 'hash'> = window.location): string {
  const params = new URLSearchParams(location.search);
  OIDC_CALLBACK_PARAMS.forEach((name) => params.delete(name));
  const query = params.toString();
  return `${location.pathname}${query ? `?${query}` : ''}${location.hash}`;
}

/** `signinRedirect` arguments that bring the user back to the current page. */
export function returnToSigninArgs(): { state: ReturnToState } {
  return { state: { returnTo: currentReturnTo() } };
}

/**
 * The return target read back from a completed sign-in's `state`, or `null` when there is none
 * worth navigating to. Only a same-origin, absolute in-app path is accepted, so a crafted `state`
 * can never turn the sign-in into an open redirect (`//evil.example`, `/\evil.example` and
 * `https://evil.example` all resolve to another origin and are refused).
 */
export function safeReturnTo(state: unknown, origin: string = window.location.origin): string | null {
  const value =
    typeof state === 'object' && state !== null
      ? (state as Partial<Record<keyof ReturnToState, unknown>>).returnTo
      : null;
  if (typeof value !== 'string' || !value.startsWith('/')) {
    return null;
  }
  let url: URL;
  try {
    url = new URL(value, origin);
  } catch {
    return null;
  }
  if (url.origin !== origin) {
    return null;
  }
  const target = currentReturnTo(url);
  return target === '/' ? null : target;
}
